/**
 * Voice lines — LOCKED narrative-first copy.
 *
 * Refactored for the story-forward version. Section headings are the
 * project's actual narrative beats (Three-Act Odyssey + Brand Universe),
 * not self-pitch framings. Copy drawn directly from the brief.
 */

import type { SectionId } from "./palette";

/**
 * Section-by-section authored copy — narrative-first.
 */
export const sectionCopy: Record<SectionId, SectionCopy> = {
  s01: {
    hero: "MEYHEM LAUREN",
    subhero: "THE WHITE TRUFFLE ERA",
    meta: "REEL 01 // COLD OPEN",
    monument: "OMERTA",
  },
  s02: {
    hero: "A THREE-ACT ODYSSEY",
    subhero:
      "A series of three short film vignettes that culminate in a single feature film — following a protagonist's journey from an enforcer to a global mogul.",
    meta: "REEL 02",
    monument: "TRIBUTO",
  },
  s03: {
    hero: "THE MUSCLE",
    subhero: "ACT I — THE LUCA BRASI PHASE",
    meta: "REEL 03 // ACT I",
    monument: "FAMIGLIA",
  },
  s04: {
    hero: "THE OWNER",
    subhero: "ACT II — THE UNCOOKED GEMS PHASE",
    meta: "REEL 04 // ACT II",
    monument: "LA NOTTE",
  },
  s05: {
    hero: "THE BUSINESS MAN",
    subhero: "ACT III — THE WHITE TRUFFLE WARS",
    meta: "REEL 05 // ACT III",
    monument: "ODISSEA",
  },
  s05b: {
    hero: "THE FILMS",
    subhero:
      "Three vignettes. One odyssey. From a Queens backroom to the truffle hills of Alba to the coffee plantations of Huila.",
    meta: "REEL 05B // THE FILMS",
    monument: "ROTTA",
  },
  s06: {
    hero: "FOOD NOIR",
    subhero: "Visual aesthetic & character archetype.",
    meta: "REEL 06 // AESTHETIC",
    monument: "NOIR",
  },
  s06b: {
    hero: "ONE LANGUAGE",
    subhero:
      "The same visual DNA across film, poster, product, merch, and digital. Every surface speaks the same dialect.",
    meta: "REEL 06B // DESIGN",
    monument: "LINGUA",
  },
  s07: {
    hero: "WHITE TRUFFLE",
    subhero: "The label.",
    meta: "REEL 07",
    monument: "RELIQUIA",
    products: {
      p1: "OIL",
      p2: "RISERVA",
      p3: "KNIFE TALK",
    },
  },
  s08: {
    hero: "THE LIFESTYLE ECOSYSTEM",
    subhero: "Brand universe & merchandise strategy.",
    meta: "REEL 08 // THE UNIVERSE",
    monument: "LINGUA",
  },
  s09: {
    hero: "LET'S TALK.",
    meta: "REEL 11 // FIN",
    monument: "FINE",
    contact: "NELOGGN@GMAIL.COM",
  },
  s10: {
    hero: "LA FAMIGLIA",
    subhero: "The team.",
    meta: "REEL 09",
    monument: "FAMIGLIA",
  },
  s11: {
    hero: "THE CANON",
    subhero: "The reference library. Click anything.",
    meta: "REEL 10",
    monument: "CANONE",
  },
} as const;

/**
 * The three acts — the full narrative content rendered in S03/S04/S05.
 * Each act's section page renders a top title + rich subtext body.
 * Content drawn directly from the brief.
 */
export const actStories = {
  act1: {
    num: "ACT I",
    title: "THE MUSCLE",
    phase: 'The "Luca Brasi" Phase',
    story:
      "A lonely hitman and chef who collects pigeons while working for an Italian mob-connected restaurant. He is the muscle — but possesses immense culinary talent and is tired of his hustle benefiting a boss who exploits him.",
    beat: "THE GOAL",
    beatText: "His desire to break free and become his own boss.",
  },
  act2: {
    num: "ACT II",
    title: "THE OWNER",
    phase: 'The "Uncooked Gems" Phase',
    story:
      "He opens his own restaurant but remains under the thumb of dealers, critics, and mafia. He seeks out a legendary, off-the-grid Sensei to learn a secret epic dish to impress a critic and make a name.",
    beat: "THE CONFLICT",
    beatText:
      'High-stakes "chef beef" and sketchy transactions involving backdoor caviar and white truffles.',
  },
  act3: {
    num: "ACT III",
    title: "THE BUSINESS MAN",
    phase: 'The "White Truffle Wars"',
    story:
      "He has expanded his network globally, launching the White Truffle — a brand he sells at his restaurant but now wants to go directly to the source in Italy, Colombia, and other countries. He has to negotiate with the local mob and earn their trust to become the bridge.",
    beat: "THE CONFLICT",
    beatText:
      "A White Truffle War with the local food mafias in Italy, and high-stakes negotiations with coffee cartels in Colombia who control the best crops.",
  },
} as const;

/**
 * Food Noir aesthetic breakdown — rendered in S06 as three bullets.
 */
export const foodNoirPillars = [
  {
    title: "FOOD NOIR AESTHETIC",
    body: 'Inspired by Nicolas Winding Refn — high-contrast lighting, a moody "vignette" structure, and a "Leon the Professional" vibe. Stylized. Gritty.',
  },
  {
    title: "THE HERO'S JOURNEY",
    body: "The character evolves from using violence (The Muscle) to calculated business moves and humility to navigate global markets.",
  },
  {
    title: "AGAINST ALL ODDS",
    body: 'He faces doubt from external forces — people doubting a "black dude" making Greek olive oil or Colombian coffee — and must show and prove through mastery of the craft.',
  },
] as const;

/**
 * Brand Universe & Merch Strategy content — rendered in S08.
 * Modeled after Action Bronson's olive oil, DJ Muggs wine drops, Alchemist merch.
 * The thesis: a lifestyle ecosystem, not standard merchandise.
 */
export const brandUniverse = {
  thesis:
    'The project aims to build a "lifestyle ecosystem" rather than standard merchandise — modeled after successful rapper-led brands like Action Bronson\'s olive oil, DJ Muggs\' wine drops, and The Alchemist\'s merch. Every service and product reinforces the "Food Noir" story.',
  groups: [
    {
      label: "THE PRODUCT LINE",
      sub: 'THE "WHITE TRUFFLE" LABEL',
      items: [
        {
          name: "Signature Consumables",
          body: "High-end White Truffle Oil, boutique olive oils (inspired by Action Bronson's $250 Grove and Vine collaboration), and limited-edition Colombian single-origin coffee.",
        },
        {
          name: "Beverages",
          body: 'Signature "White Truffle" Red and White wines — in the lineage of DJ Muggs\' Notes and Tones and Freddie Gibbs\' Alfredo wine releases.',
        },
        {
          name: "Specialized Gear",
          body: '"Knife Talk" kitchen equipment, featuring professional knives sharpened at precise angles.',
        },
        {
          name: "Food",
          body: "Exclusive boutique connoisseur product releases.",
        },
        {
          name: "Lifestyle & Media",
          body: "Branded kitchen equipment; chefs using the product.",
        },
        {
          name: "Limited Vinyl Variants",
          body: "Translucent Yellow 2LP colored vinyl. Laser Disc cover. Signed editions.",
        },
      ],
    },
    {
      label: "ACTIVATIONS",
      sub: "IMMERSIVE EXPERIENCE",
      items: [
        {
          name: "Watch The Film Portal",
          body: 'A "Watch the Film" portal for the three shorts and an interactive map of the Odyssey. The website functions as a narrative extension of the Food Noir universe — not a store.',
        },
        {
          name: "Real-World Pop-Ups",
          body: 'Serving the "epic dish" from the film in physical restaurants, paired with food product releases.',
        },
        {
          name: "Film Festival Submission",
          body: "Submission strategy for the short-film trilogy across the festival circuit.",
        },
        {
          name: "Hybrid Variety/Reality Show",
          body: 'Filming the "making of" the brand as a hybrid Variety/Reality show that bridges the gap between the fictional character and Meyhem\'s real-world business ventures.',
        },
      ],
    },
  ],
  close:
    "This approach transforms the project from three films into a comprehensive lifestyle label, where every service and product reinforces the Food Noir story.",
} as const;

/**
 * The real crew — rendered in S10 La Famiglia section.
 * Portrait filenames are referenced by key — resolved to URLs in the S10
 * component via ESM imports so Vite's asset pipeline bundles them.
 */
export const crew = [
  {
    name: "GIANCARLO LOFFREDO",
    role: "CREATIVE DIRECTOR",
    portrait: "giancarlo",
  },
  {
    name: "NELO G",
    role: "CREATIVE DIRECTOR",
    portrait: "nelo",
  },
  {
    name: "JN SILVA",
    role: "WEB DEVELOPER / CONTENT STRATEGIST",
    portrait: "jnsilva",
  },
  {
    name: "TOMALL",
    role: "BRAND DESIGN",
    portrait: "tomall",
  },
] as const;

/**
 * Persistent frame copy — the surveillance dossier layer across all sections.
 */
export const frameCopy = {
  motto: "ACTA NON VERBA",
  watermark: "DO NOT DISTRIBUTE",
  issue: "VOL.VII // NO.04",
  system: "SYS.OP.492",
  coordOrigin: {
    lat: 40.7306,
    lng: -73.9352,
    label: "40°43'50\"N 73°56'06\"W",
  },
} as const;

export type SectionCopy = {
  hero: string;
  subhero?: string;
  voiceQuote?: string;
  meta: string;
  monument: string;
  panels?: Record<string, string>;
  products?: Record<string, string>;
  contact?: string;
  tag?: string;
};

/**
 * Anti-patterns — phrases we never use.
 */
export const bannedCopyPatterns = [
  /!/,
  /mindful/i,
  /journey/i,
  /vibe$/i,
  /let's go/i,
  /get started/i,
  /contact us/i,
  /learn more/i,
  /dream big/i,
  /stay hungry/i,
] as const;
